import { StatusVariant, SelectOption } from '@/types/common';
import { PropertyTable, TableFilter } from '../metadata/propertyConfig';
import { TableRowActionOption as Options } from '@/components/ui/table-row-actions';
import { OrderStatusEnum } from '../../../orders/table-cell/model/orderCell';
import { IconComponent } from '../../../../util/icons';
import cancelIcon from '../../../../asset/icons/cancel-order.svg';
import manageAssetsIcon from '../../../../asset/icons/manage-assets.svg';
import { Services } from '../../model/propertiesModel';
import type { ProductService, ServiceCategory } from '@/types/order.types';
import type { SortState } from '@/types/table';

import { Pencil } from 'lucide-react';

// ---------------------------------------------------------------------------
// Filter helpers
// ---------------------------------------------------------------------------

const getFilterStatus = (activeStatus: string): string => {
  return activeStatus === 'Active Units' ? 'true' : 'false';
};

export const getFilterChain = (
  filter: TableFilter,
  moreFilters: string,
  isProperty?: boolean
): string => {
  let apiFilter = '';

  if (filter.address) {
    apiFilter += isProperty
      ? 'searchQuery=' + encodeURIComponent(filter.address)
      : 'address=' + encodeURIComponent(filter.address);
  }

  if (filter.entityUUIDs && filter.entityUUIDs.length > 0) {
    if (apiFilter) apiFilter += '&';
    apiFilter += filter.entityUUIDs.map((id) => 'entityUUIDs=' + id).join('&');
  }

  if (filter.status) {
    if (apiFilter) apiFilter += '&';
    apiFilter += 'propertyStatus=' + encodeURIComponent(filter.status);
  }

  if (filter.orderDateFrom) {
    if (apiFilter) apiFilter += '&';
    apiFilter += 'orderDateFrom=' + filter.orderDateFrom;
  }

  if (filter.orderDateTo) {
    if (apiFilter) apiFilter += '&';
    apiFilter += 'orderDateTo=' + filter.orderDateTo;
  }

  if (filter.orderStatus) {
    if (apiFilter) apiFilter += '&';
    if (Array.isArray(filter.orderStatus)) {
      apiFilter += filter.orderStatus
        .map((s) => 'orderStatuses=' + s)
        .join('&');
    } else {
      apiFilter += 'orderStatuses=' + filter.orderStatus;
    }
  }

  if (filter.activeStatus) {
    if (apiFilter) apiFilter += '&';
    apiFilter += 'isActive=' + getFilterStatus(filter.activeStatus);
  }

  if (apiFilter) apiFilter = '&' + apiFilter;
  if (moreFilters) apiFilter += '&' + moreFilters;

  return apiFilter;
};

// ---------------------------------------------------------------------------
// Sort helpers
// ---------------------------------------------------------------------------

export const getSortByString = (name: string, asc: boolean): string => {
  return `sortBy=${name}&sortDir=${asc ? 'asc' : 'desc'}`;
};

/**
 * Inverse of getSortByString. `null` unless the string carries a non-empty
 * `sortBy` and a lowercase `asc`/`desc` `sortDir`; callers read null as
 * "no explicit sort" (DV-3122).
 */
export const parseSortByString = (sortString: string): SortState | null => {
  const params = new URLSearchParams(sortString);
  const selector = params.get('sortBy');
  const dir = params.get('sortDir');
  if (!selector || (dir !== 'asc' && dir !== 'desc')) return null;
  return { selector, asc: dir === 'asc' };
};

export const getSortOptions = (
  option: Options | null,
  isProperty: boolean
): Options[] => {
  const sort = isProperty
    ? [
        { text: 'Last Modified', key: 'updatedAt' },
        { text: 'Address', key: 'address1' },
        { text: 'Available Date', key: 'availableDate' },
        { text: 'Lease End Date', key: 'leaseEndDate' },
        { text: 'Portfolio Name', key: 'portfolioName' },
        { text: 'Sync Property', key: 'propertySource' }
      ]
    : [
        { text: 'Order Date', key: 'orderDate' },
        { text: 'Order ID', key: 'orderNumber' },
        { text: 'Address', key: 'propertyAddress' },
        { text: 'Order Status', key: 'orderStatus' },
        { text: 'Appointment Date', key: 'appointments.dateStartLocal' },
        { text: 'Order Completed Date', key: 'orderCompletedDate' }
      ];

  return sort.map((data: Record<string, unknown>) => {
    (data as Record<string, unknown>).active =
      option && data.text === option.text;
    (data as Record<string, unknown>).asc = option?.asc;
    return data;
  }) as Options[];
};

// ---------------------------------------------------------------------------
// Status helpers
// ---------------------------------------------------------------------------

export const getPropertyStatusSelectOptions = (
  statusList: string[]
): SelectOption[] => {
  return statusList.map(
    (status: string) => ({ value: status, label: status }) as SelectOption
  );
};

export const getPropertyStatusState = (status: string): StatusVariant => {
  if (status === 'Vacant') return 'destructive';
  if (status === 'Occupied') return 'success';
  return 'warning';
};

export const getServices = (services: Services[]): string[] => {
  return services.map((service: Services) => service.name || '');
};

// A service line item as it appears on an order. Connect-native orders carry
// `shoppingCartSlug`; legacy-migrated orders may instead carry `slug` and may
// omit `quantity` entirely (see the ~0.85% of QA line items on `slug`).
export type OrderedServiceItem = {
  name?: string;
  shoppingCartSlug?: string;
  slug?: string;
  quantity?: number | null;
};

// A service line item resolved for display in the "Services Ordered" column.
export type OrderedServiceDisplay = {
  name: string;
  category: ServiceCategory;
  // The selected quantity to show, or null when it shouldn't appear: a
  // fixed-quantity service, a missing quantity, or before the catalog loads.
  quantity: number | null;
};

// Resolve each ordered service against the product catalog into its display
// category and quantity. The quantity is shown only when it was actually
// user-selectable — a service with more than one valid quantity let the
// customer choose, so the number is meaningful; a fixed-quantity service
// (e.g. Zillow 3D, validQuantities = [1]) would just show a noisy "1". The
// catalog is keyed by shoppingCartSlug, with a fallback to the legacy `slug`.
export const getOrderedServices = (
  services: OrderedServiceItem[],
  catalog: ProductService[] | undefined,
): OrderedServiceDisplay[] => {
  const catalogBySlug = new Map<
    string,
    { validQuantityCount: number; isAsset: boolean }
  >();
  (catalog ?? []).forEach((service) => {
    if (service.shoppingCartSlug) {
      catalogBySlug.set(service.shoppingCartSlug, {
        validQuantityCount: service.validQuantities?.length ?? 0,
        isAsset: service.serviceType?.toLowerCase() === 'asset',
      });
    }
  });

  return services.map((service) => {
    const slug = service.shoppingCartSlug || service.slug;
    const catalogEntry = slug ? catalogBySlug.get(slug) : undefined;
    const isSelectable = (catalogEntry?.validQuantityCount ?? 0) > 1;
    return {
      name: service.name || '',
      category: catalogEntry?.isAsset ? 'asset' : 'property',
      quantity:
        isSelectable && service.quantity != null ? service.quantity : null,
    };
  });
};

export const getCardNumber = (cardNumber: string): string => {
  return `************${cardNumber}`;
};

export const capitalizeFirstLetter = (cardType: string): string => {
  if (!cardType || cardType.length === 0) return '';
  return cardType.charAt(0).toUpperCase() + cardType.slice(1).toLowerCase();
};

export const getUnableToComplete = (
  unableToComplete: boolean | null
): string => {
  if (unableToComplete === true) return 'Yes';
  if (unableToComplete === null) return '-';
  return 'No';
};

// ---------------------------------------------------------------------------
// More-view action options
// ---------------------------------------------------------------------------

// Orders-only: the Properties list replaced its 3-dot Quick Actions column
// with the per-row Add to Cart button + Recent Assets icons, so only the
// Orders table still renders a more-options menu.
export const getOptionsForMoreView = (
  orderStatus: PropertyTable['orderStatus']
): Options[] => {
  const ordersOptions: Options[] = [
    {
      id: 'edit-order',
      text: 'Edit Order',
      icon: <Pencil className="h-5 w-5" />,
      disabled: false
    }
  ];

  if (
    orderStatus?.data === OrderStatusEnum.RECEIVED ||
    orderStatus?.data === OrderStatusEnum.SCHEDULED ||
    orderStatus?.data === 'order.processed'
  ) {
    ordersOptions.push({
      id: 'cancel-order',
      text: 'Cancel Order',
      icon: (
        <IconComponent
          src={cancelIcon}
          alt="Cancel Order"
          width={23}
          height={23}
        />
      ),
      disabled: false
    });
  }

  if (
    orderStatus?.data === OrderStatusEnum.DELIVERED ||
    orderStatus?.data === 'order.verified'
  ) {
    ordersOptions.push({
      id: 'manage-assets',
      text: 'Manage Assets',
      icon: (
        <IconComponent
          src={manageAssetsIcon}
          alt="manageAssetsIcon"
          width={23}
          height={23}
        />
      ),
      disabled: false
    });
  }

  return ordersOptions;
};

